import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { SimpleChart } from '~/components/Shared/Charts/SimpleChart';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { CrudForm } from '~/eds/src/modules/CrudForm/CrudForm';
import { Option } from '~/eds/src/types/Option';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { SearchQuery } from '~/features/advanced-search/types';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { State as XRayState } from '~/redux/slices/x-ray/types';
import { Bucket, ChartType } from '~/types/DashboardV2';
import { Nullable } from '~/types/Nullable';

import { PromptModelConfig } from '../../../../redux/api/types/sylvanus/model';
import { MODEL_INSTRUCTIONS_ELEMENT_ID } from '../../constants';
import { InstructionsEditor } from '../../ModelDetails/Configuration/InstructionsEditor';
import { LibraryModelVersion, Tag } from '../types';
import { useGetCategoryOptions } from './useGetCategoryOptions';

interface Props {
  config: Nullable<XRayState['configuration'] | PromptModelConfig>; // TODO: Update to PromptModelConfig
  isSaving: boolean;
  libraryModel: Nullable<LibraryModelVersion>;
  saveLibraryModel: any; // MutationTrigger type is not exported from redux
  fieldName?: Nullable<string>;
  isLoadingFilters?: boolean;
}

export const useLibraryModelDetailsModal = ({
  config,
  fieldName = null,
  isSaving,
  libraryModel,
  saveLibraryModel,
}: Props): [React.ReactNode, () => void, () => void] => {
  const { toast } = useToast();
  const intl = useIntl();
  const { RETRY_ERROR } = useErrors();

  const [skip, setSkip] = useState(false);
  const { options: categoryOptions } = useGetCategoryOptions({ skip });

  const [options, setOptions] = useState<Option<string>[]>([]);

  useEffect(() => {
    if (!skip && categoryOptions.length) {
      setSkip(true);
      const updatedOptions = [...categoryOptions, ...options];
      if (!isEqual(options, updatedOptions)) {
        setOptions(updatedOptions);
      }
    }
  }, [categoryOptions]);

  const [configId, setConfigId] = useState<string>('');
  const [isConfigCheckSuccess, setIsConfigCheckSuccess] = useState(false);
  const [isConfigCheckError, setIsConfigCheckError] = useState(false);
  const [name, setName] = useState(fieldName);
  const [instructions, setInstructions] = useState<Nullable<string>>(null);
  const [description, setDescription] = useState('');
  const [changelog, setChangelog] = useState('Initial version');
  const [query, setQuery] = useState<SearchQuery>([]);
  const [tags, setTags] = useState<Nullable<string[]>>(null);
  const [updateType, setUpdateType] = useState<'major' | 'minor' | 'patch'>(
    'patch',
  );
  const [sampleOutputs, setSampleOutputs] = useState<Nullable<string>>(null);
  const [chartType, setChartType] = useState<Nullable<ChartType>>(null);
  const [chartData, setChartData] = useState<
    { label: string; value: Nullable<number> }[]
  >([{ label: '', value: null }]);

  const [checkConfig, { isLoading: isCheckingConfig }] =
    api.endpoints.checkLibraryModelConfig.useMutation();

  useEffect(() => {
    if (libraryModel) {
      setConfigId('');
      setIsConfigCheckError(false);
      setIsConfigCheckSuccess(false);
      setName(libraryModel.label);
      setDescription(libraryModel.description ?? '');
      setChangelog('');
      setInstructions(config?.instructions ?? null);
      setQuery(
        (libraryModel.config.scope?.data.attributes.query as SearchQuery) ?? [],
      );
      setTags(
        libraryModel.tags ? libraryModel.tags.map((t: Tag) => t.label) : null,
      );
      setSampleOutputs(libraryModel.sampleOutputs?.text ?? null);
      setChartType(libraryModel.sampleOutputs?.dashboard?.type ?? null);
      setChartData(
        libraryModel.sampleOutputs?.dashboard?.buckets ?? [
          { label: '', value: null },
        ],
      );
    }
  }, [libraryModel]);

  const [
    libraryModelDetails,
    showLibraryModelDetails,
    hideLibraryModelDetails,
  ] = useModal({
    children: (
      <Layout preset="form-fields">
        <Text>
          {libraryModel
            ? intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.confirmLibraryModelDetailsText',
                defaultMessage:
                  'Confirm the library model details, describe the changes and click Save & Publish to create a new version of the existing library model and publish it to the Model Library.',
                description: 'Instructions for updating existing library model',
              })
            : intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.provideAdditionalDetailsText',
                defaultMessage:
                  'Provide additional details to create a new library model and publish it to the Model Library.',
                description: 'Instructions for creating new library model',
              })}
        </Text>
        {libraryModel && (
          <Layout spacing={2}>
            <FormField<string, false>
              description={intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.configIdDescription',
                defaultMessage:
                  "Validate a configuration ID to check for compatibility. A valid ID updates the model's internal system configuration on Save & Publish.",
                description: 'Description for the configuration ID field',
              })}
              error={
                isConfigCheckError
                  ? intl.formatMessage({
                      id: 'UseLibraryModelDetailsModal.configIdError',
                      defaultMessage:
                        'The configuration ID could not be validated. Check for errors or try a different ID.',
                      description:
                        'Error message when the configuration update fails',
                    })
                  : undefined
              }
              input={TextInput}
              inputProps={{
                placeholder: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.configIdPlaceholder',
                  defaultMessage: 'Paste a configuration ID',
                  description:
                    'Placeholder text for the configuration ID field',
                }),
              }}
              label={intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.configIdLabel',
                defaultMessage: 'Configuration ID',
                description: 'Label for the configuration ID field',
              })}
              name="library-model-config-id-input"
              success={
                isConfigCheckSuccess
                  ? intl.formatMessage({
                      id: 'UseLibraryModelDetailsModal.configIdSuccess',
                      defaultMessage: 'Configuration ID validated.',
                      description:
                        'Success message when configuration ID is validated',
                    })
                  : undefined
              }
              value={configId}
              onChange={(updatedConfig) => {
                setConfigId(updatedConfig ?? '');
                setIsConfigCheckSuccess(false);
                setIsConfigCheckError(false);
              }}
            />
            <Box pt="64px">
              <Button
                level="secondary"
                isLoading={isCheckingConfig}
                disabled={!configId}
                text={
                  isCheckingConfig
                    ? intl.formatMessage({
                        id: 'useLibraryModelDetailsModal.checkConfigUpdatingButton',
                        defaultMessage: 'Validating',
                        description:
                          'Button text while the configuration ID is being validated',
                      })
                    : intl.formatMessage({
                        id: 'useLibraryModelDetailsModal.checkConfigUpdateButton',
                        defaultMessage: 'Validate',
                        description:
                          'Button text for validating the configuration ID',
                      })
                }
                onClick={() =>
                  checkConfig({
                    config: {
                      ...config,
                      internal: configId || undefined,
                    } as PromptModelConfig,
                    name: libraryModel.label,
                  })
                    .unwrap()
                    .then(
                      () => {
                        // success
                        setIsConfigCheckError(false);
                        setIsConfigCheckSuccess(true);
                      },
                      () => {
                        // error
                        setIsConfigCheckError(true);
                        setIsConfigCheckSuccess(false);
                      },
                    )
                }
              />
            </Box>
          </Layout>
        )}
        <FormField<string, false>
          description={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.modelNameDescription',
            defaultMessage:
              'Set the name of the library model. If not specified, the original data field name will be used.',
            description: 'Description for model name field',
          })}
          info={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.modelNameDescriptionInfo',
            defaultMessage:
              'Updates to Data Fields are not synced in Model Library. If you change the name of a data field after publishing the library model, it will not automatically update the name of the library model.',
            description: 'Description for model name field info',
          })}
          input={TextInput}
          inputProps={{
            placeholder: libraryModel ? libraryModel.label : fieldName,
          }}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.libraryModelNameLabel',
            defaultMessage: 'Name',
            description: 'Label for library model name field',
          })}
          name="library-model-name-input"
          value={name}
          onChange={setName}
        />
        {libraryModel && (
          <>
            <FormField<string, false>
              input={InstructionsEditor}
              label={intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.instructionsLabel',
                defaultMessage: 'Instructions for the model',
                description: 'Label for model instructions field',
              })}
              name={MODEL_INSTRUCTIONS_ELEMENT_ID}
              value={instructions}
              onChange={setInstructions}
            />
            <QueryFiltersFormField
              required
              defaultFilters={['Contract Type']}
              description={intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.scopeDescription',
                defaultMessage:
                  'Use filters to choose which documents the model will run on.',
                description: 'Description for scope filters',
              })}
              mode="preview"
              optional={false}
              query={query}
              title={intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.scopeTitle',
                defaultMessage: 'Scope',
                description: 'Title for scope section',
              })}
              onQueryChange={setQuery}
            />
          </>
        )}
        <FormField<string, false>
          required
          description={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.descriptionFieldDescription',
            defaultMessage: 'Describe the library model.',
            description: 'Description for description field',
          })}
          input={TextInput}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.descriptionLabel',
            defaultMessage: 'Description',
            description: 'Label for description field',
          })}
          name="library-model-description-input"
          value={description}
          onChange={(updatedDescription) =>
            setDescription(updatedDescription ?? '')
          }
        />
        <FormField<string, true>
          required
          description={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.categoryDescription',
            defaultMessage:
              'Choose the category or categories this library model will be included in.',
            description: 'Description for category selection',
          })}
          input={Select}
          inputProps={{
            isMulti: true,
            isCreatable: true,
            options,
            placeholder: intl.formatMessage({
              id: 'UseLibraryModelDetailsModal.categoryPlaceholder',
              defaultMessage: 'Select or create a category...',
              description: 'Placeholder for category select',
            }),
            onAddOption: (newCategory: Option<string>) =>
              setOptions([...options, newCategory]),
          }}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.categoryLabel',
            defaultMessage: 'Category',
            description: 'Label for category field',
          })}
          name="library-model-categories-select"
          value={tags}
          onChange={setTags}
        />
        <FormField<string, false>
          description={
            libraryModel
              ? intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.changelogDescriptionUpdate',
                  defaultMessage:
                    'Describe the changes in this version of the library model.',
                  description: 'Changelog description for updates',
                })
              : intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.changelogDescriptionInitial',
                  defaultMessage:
                    'Describe the initial version of the library model.',
                  description: 'Changelog description for initial version',
                })
          }
          input={TextInput}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.changelogLabel',
            defaultMessage: 'Changelog',
            description: 'Label for changelog field',
          })}
          name="library-model-changelog-input"
          value={changelog}
          onChange={(updatedChangelog) => setChangelog(updatedChangelog ?? '')}
        />
        {libraryModel && (
          <FormField<string, false>
            description={intl.formatMessage({
              id: 'UseLibraryModelDetailsModal.updateTypeDescription',
              defaultMessage:
                'Select the type of update for this version of the library model.',
              description: 'Description for update type selection',
            })}
            input={Select}
            inputProps={{
              options: [
                {
                  description: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.majorVersionExample',
                    defaultMessage: 'e.g. 1.0.0 > 2.0.0',
                    description: 'Example for major version update',
                  }),
                  label: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.majorLabel',
                    defaultMessage: 'Major',
                    description: 'Label for major version update',
                  }),
                  value: 'major',
                },
                {
                  description: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.minorVersionExample',
                    defaultMessage: 'e.g. 1.0.0 > 1.1.0',
                    description: 'Example for minor version update',
                  }),
                  label: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.minorLabel',
                    defaultMessage: 'Minor',
                    description: 'Label for minor version update',
                  }),
                  value: 'minor',
                },
                {
                  description: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.patchVersionExample',
                    defaultMessage: 'e.g. 1.0.0 > 1.0.1',
                    description: 'Example for patch version update',
                  }),
                  label: intl.formatMessage({
                    id: 'UseLibraryModelDetailsModal.patchLabel',
                    defaultMessage: 'Patch',
                    description: 'Label for patch version update',
                  }),
                  value: 'patch',
                },
              ],
            }}
            label={intl.formatMessage({
              id: 'UseLibraryModelDetailsModal.updateTypeLabel',
              defaultMessage: 'Update Type',
              description: 'Label for update type field',
            })}
            name="library-model-version-update-type-select"
            value={updateType}
            onChange={(updatedType) => {
              if (updatedType) {
                setUpdateType(updatedType as 'major' | 'minor' | 'patch');
              }
            }}
          />
        )}
        <FormField<Nullable<string>, false>
          optional
          description={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.sampleOutputsDescription',
            defaultMessage:
              'Enter the sample outputs that will be displayed in the library model preview. Markdown formatting is supported.',
            description: 'Description for sample outputs field',
          })}
          input={TextArea}
          inputProps={{
            placeholder: intl.formatMessage({
              id: 'UseLibraryModelDetailsModal.sampleOutputsPlaceholder',
              defaultMessage:
                'E.g. **bold text**, *italicized text*, ~strikethrough text~ 1. numbered item (two spaces followed by Shift+Enter for new line)   2. second item   - bulleted list item',
              description: 'Placeholder for sample outputs textarea',
            }),
            rows: 4,
          }}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.sampleOutputsLabel',
            defaultMessage: 'Sample Outputs',
            description: 'Label for sample outputs field',
          })}
          name="library-model-sample-outputs-input"
          value={sampleOutputs}
          onChange={setSampleOutputs}
        />
        <FormField<ChartType, false>
          optional
          description={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.chartVisualDescription',
            defaultMessage:
              'Configure the chart that will be displayed in the library model preview.',
            description: 'Description for chart visual field',
          })}
          input={Select}
          inputProps={{
            isClearable: true,
            isMulti: false,
            options: [
              {
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.horizontalBarLabel',
                  defaultMessage: 'Horizontal Bar',
                  description: 'Label for horizontal bar chart option',
                }),
                value: 'horizontal_bar',
              },
              {
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.verticalBarLabel',
                  defaultMessage: 'Vertical Bar',
                  description: 'Label for vertical bar chart option',
                }),
                value: 'vertical_bar',
              },
              {
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.donutLabel',
                  defaultMessage: 'Donut',
                  description: 'Label for donut chart option',
                }),
                value: 'donut',
              },
              {
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.lineLabel',
                  defaultMessage: 'Line',
                  description: 'Label for line chart option',
                }),
                value: 'line',
              },
            ],
          }}
          label={intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.chartVisualLabel',
            defaultMessage: 'Chart Visual',
            description: 'Label for chart visual field',
          })}
          name="library-model-chart-type-select"
          value={chartType}
          onChange={setChartType}
        />
        {Boolean(chartType) && (
          <CrudForm<any, false, Bucket>
            actions={{
              add: {
                text: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.addDataPointText',
                  defaultMessage: 'Add Data Point',
                  description: 'Text for add data point button',
                }),
              },
              delete: (_datum) => ({
                tooltip: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.removeDataPointTooltip',
                  defaultMessage: 'Remove data point.',
                  description: 'Tooltip for remove data point action',
                }),
              }),
            }}
            columns={[
              {
                key: 'label',
                input: TextInput,
                inputProps: {
                  width: 'm',
                },
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.dataPointLabelLabel',
                  defaultMessage: 'Label',
                  description: 'Label for data point label field',
                }),
                name: 'library-model-chart-data-label-input',
                placeholder: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.dataPointLabelPlaceholder',
                  defaultMessage: 'Label the data point.',
                  description: 'Placeholder for data point label',
                }),
                value: (datum: Bucket) => datum.label,
                onChange: () => {},
              },
              {
                key: 'value',
                input: NumberInput,
                inputProps: {
                  width: 'm',
                },
                label: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.dataPointValueLabel',
                  defaultMessage: 'Value',
                  description: 'Label for data point value field',
                }),
                name: 'library-model-chart-data-value-input',
                placeholder: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.dataPointValuePlaceholder',
                  defaultMessage: 'Enter a numerical value.',
                  description: 'Placeholder for data point value',
                }),
                value: (datum: Bucket) => datum.value,
                onChange: () => {},
              },
            ]}
            columnWidths={['1fr', '1fr']}
            data={chartData as Bucket[]}
            onUpdate={setChartData}
          />
        )}
        {Boolean(chartType) && (
          <SimpleChart
            buckets={
              chartData.map((datum) => ({
                label: datum.label ?? '',
                value: datum.value ?? 0,
              })) as Bucket[]
            }
            type={chartType as ChartType}
            width={400}
          />
        )}
      </Layout>
    ),
    title: intl.formatMessage({
      id: 'UseLibraryModelDetailsModal.modalTitle',
      defaultMessage: 'Additional Library Model Details',
      description: 'Title for library model details modal',
    }),
    primaryAction: {
      disabled: isSaving || !description || !tags,
      tooltip:
        !description && !tags
          ? intl.formatMessage({
              id: 'UseLibraryModelDetailsModal.addDescriptionAndCategoryTooltip',
              defaultMessage: 'Add a description and select a category.',
              description:
                'Tooltip when both description and category are missing',
            })
          : !description
            ? intl.formatMessage({
                id: 'UseLibraryModelDetailsModal.addDescriptionTooltip',
                defaultMessage: 'Add a description.',
                description: 'Tooltip when description is missing',
              })
            : !tags
              ? intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.selectCategoryTooltip',
                  defaultMessage: 'Select a category.',
                  description: 'Tooltip when category is missing',
                })
              : undefined,
      text: isSaving
        ? intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.savingText',
            defaultMessage: 'Saving…',
            description: 'Text when saving library model',
          })
        : intl.formatMessage({
            id: 'UseLibraryModelDetailsModal.saveAndPublishText',
            defaultMessage: 'Save & Publish',
            description: 'Text for save and publish button',
          }),
      onClick: () => {
        const activeQuery = isActiveQuery(query);
        saveLibraryModel({
          config,
          description,
          changelog,
          instructions: instructions ?? undefined,
          internal: configId || undefined,
          libraryModelId: libraryModel?.libraryModelId ?? null,
          name: name || libraryModel?.label || fieldName,
          query: activeQuery.length ? activeQuery : undefined,
          sampleOutputs: {
            buckets: chartData.map((datum) => ({
              label: datum.label,
              value: datum.value ?? 0,
            })) as Bucket[],
            text: sampleOutputs ?? undefined,
            type: chartType ?? undefined,
          },
          tags: tags!,
          updateType,
        })
          .unwrap()
          .then((savedLibraryModel: LibraryModelVersion) => {
            if (savedLibraryModel) {
              toast({
                message: intl.formatMessage({
                  id: 'UseLibraryModelDetailsModal.saveSuccessMessage',
                  defaultMessage: 'Library Model saved successfully.',
                  description: 'Success message when library model is saved',
                }),
                status: 'success',
              });
              hideLibraryModelDetails();
              setChangelog('');
            }
          })
          .catch((error: any) => {
            const errorMessage = error.response?.data?.detail ?? RETRY_ERROR;
            toast({
              message: intl.formatMessage(
                {
                  id: 'UseLibraryModelDetailsModal.saveErrorMessage',
                  defaultMessage: 'Error saving library model. {errorMessage}',
                  description:
                    'Error message when saving library model fails. Example: errorMessage = "Network error"',
                },
                {
                  errorMessage,
                },
              ),
              status: 'danger',
            });
          });
      },
    },
  });

  return [
    libraryModelDetails,
    showLibraryModelDetails,
    hideLibraryModelDetails,
  ];
};
